import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import classnames from 'classnames';
import truncate from 'lodash/truncate';
import CellTower from '@mui/icons-material/CellTower';
import EventBusy from '@mui/icons-material/EventBusy';
import Folder from '@mui/icons-material/Folder';
import HighlightOff from '@mui/icons-material/HighlightOff';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { createIconComponent } from 'uikit/icons';
import { Typo } from 'uikit/content';
import { Tooltip } from 'uikit/tooltips';

import getIsAgendaItem from '../helpers/getIsAgendaItem';

import VideosAdminListDragAndDropContentPreview from './videosAdminListDragAndDropContentPreview';
import VideoShortcutTag from './videoShortcutTag';

import styles from '../styles/videosAdminListDragAndDropContent.module.scss';

const VideosAdminListDragAndDropContent = ({ item, onContentActionClick }) => {
  const { data } = item;

  const {
    id,
    title,
    description,
    code,
    metadata,
    type,
    created_by: createdBy,
    formattedUpdatedAt: updatedAt,
    cover_art: coverArt,
    formattedStartAt: startAt,
    status,
  } = data;
  const hasEditorPermission = data.metadata?.has_editor_permission || data.has_editor_permission;
  const params = useParams();
  const { orgId, type: videosType } = params;
  const isInLiveMode = videosType === 'live';
  const isAgendaItem = getIsAgendaItem(params);
  const isTopic = data.type === 'topic';
  const isShortcut = metadata?.is_shortcut;
  const tabId = isShortcut ? 'info' : 'compose';

  const to = isTopic
    ? `/videos/organizations/${orgId}/${videosType}/topics/${id}`
    : `/videos/organizations/${orgId}/${videosType}/${id}/${tabId}`;

  let mainIcon = isTopic ? <Folder /> : <PlayCircleOutline />;

  const isArchived = metadata?.is_archived;

  const getTooltip = () => {
    const variants = [
      {
        condition: isArchived,
        icon: <VisibilityOff />,
        text: i18n.t('archived'),
      },
      {
        condition: status === 'closed',
        icon: <EventBusy />,
        text: i18n.t('past-live-video'),
      },
      {
        condition: status === 'canceled',
        icon: <HighlightOff />,
        text: i18n.t('canceled'),
      },
    ];

    const variant = variants.find(({ condition }) => condition);

    return (
      variant && (
        <Tooltip content={variant.text}>
          {variant.icon && createIconComponent({ icon: variant.icon, className: styles.icon, size: 20 })}
        </Tooltip>
      )
    );
  };

  const hasCodeText = !!code && !isArchived && type === 'video';

  const createdByName = createdBy?.name;

  if (isInLiveMode) mainIcon = <CellTower />;

  const shouldAllowClickingContent = hasEditorPermission || type === 'topic';

  let Element = shouldAllowClickingContent ? Link : 'div';

  if (isAgendaItem) {
    mainIcon = 'agenda';
    Element = 'div';
  }

  const truncatedTitle = truncate(title || '', { length: 125 });

  const previewTrigger = React.useRef(null);

  return (
    <div className={classnames(styles.element, { [styles.isTopic]: isTopic })}>
      <div className={classnames(styles.column, styles.isGrower)}>
        {mainIcon && createIconComponent({ icon: mainIcon, className: styles.icon, size: 20 })}
        <Element className={styles.link} to={to}>
          <div className={styles.titleContainer} ref={previewTrigger}>
            <Typo
              className={classnames(styles.title, {
                [styles.isArchived]: isArchived,
                [styles.isVideoDisabled]: !shouldAllowClickingContent,
              })}
              element="h3"
              variant="s02"
              weight="medium"
              title={truncatedTitle}
            >
              {title}
            </Typo>
            {!isAgendaItem && hasEditorPermission && (
              <VideosAdminListDragAndDropContentPreview
                title={title}
                description={description}
                picture={coverArt}
                type={type}
                code={code}
                createdBy={createdBy}
                metadata={metadata}
                onContentActionClick={onContentActionClick}
                item={item}
                trigger={previewTrigger}
              />
            )}
          </div>
        </Element>
        {getTooltip()}
        {hasCodeText && (
          <Typo className={styles.code} element="p" variant="s01">
            {i18n.t('code')}
            {': '}
            {code}
          </Typo>
        )}
        {isShortcut && <VideoShortcutTag />}
      </div>
      {isInLiveMode ? (
        <Typo className={classnames(styles.column, styles.isFixed)} variant="s01">
          {startAt || '.'}
        </Typo>
      ) : (
        <>
          <Typo
            element="p"
            variant="s01"
            className={classnames(styles.updatedAt, styles.column, styles.isFixed)}
          >
            {updatedAt || '.'}
          </Typo>
          <Typo
            element="p"
            variant="s01"
            className={classnames(styles.createdByName, styles.column, styles.isFixed)}
          >
            {createdByName || '.'}
          </Typo>
        </>
      )}
      <div className={styles.placeholder} />
    </div>
  );
};

VideosAdminListDragAndDropContent.propTypes = {
  item: PropTypes.shape({
    data: PropTypes.shape({
      title: PropTypes.string,
      metadata: PropTypes.shape({
        is_shortcut: PropTypes.bool,
        is_archived: PropTypes.bool,
      }),
      has_editor_permission: PropTypes.bool,
      type: PropTypes.string,
    }),
    hasChildren: PropTypes.bool,
    isTopic: PropTypes.bool,
    id: PropTypes.number,
  }).isRequired,
};

export default React.memo(VideosAdminListDragAndDropContent);
